import React, { useState, useEffect } from "react";
import { Stack, TextInput, Text, Box, Alert, Group } from "@mantine/core";
import { IconBuilding, IconCheck, IconAlertCircle } from "@tabler/icons-react";

import { Account, AccountInput } from "@/utils/api/AccountService";
import { useAccounts } from "@/hooks";
import { useApp } from "@/contexts/AppContext";
import { AppButton } from "@/components/generic";

export interface AccountFormProps {
  /** Callback when form is submitted successfully */
  onSuccess?: (account: Account) => void;

  /** Error message to display */
  error?: string | null;

  /** Success message to display */
  success?: string | null;
}

export const AccountForm: React.FC<AccountFormProps> = ({
  onSuccess,
  error: externalError,
  success: externalSuccess,
}) => {
  const { user } = useApp();
  const { account, loading, isUpdating, lastError, get, update, clearError } =
    useAccounts({
      autoLoad: false, // We'll manually load the specific account
    });

  const [name, setName] = useState("");
  const [hasLoadedAccount, setHasLoadedAccount] = useState(false);

  // Get the first account ID from user's userAccounts
  const accountId = user?.userAccounts?.[0]?.accountId;

  // Load account data when component mounts and we have an accountId
  useEffect(() => {
    if (accountId && !hasLoadedAccount) {
      get(accountId);
      setHasLoadedAccount(true);
    }
  }, [accountId, get, hasLoadedAccount]);

  // Update form when account data changes
  useEffect(() => {
    if (account) {
      setName(account.name || "");
    }
  }, [account]);

  // Clear errors when component unmounts or account changes
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!accountId || !account) return;

    // Clear any previous errors
    clearError();

    const formData: AccountInput = {
      name: name.trim() || undefined,
    };

    try {
      const updatedAccount = await update(accountId, formData);

      if (updatedAccount && onSuccess) {
        onSuccess(updatedAccount);
      }
    } catch (error) {
      // Error is handled by the hook
      console.error("Error updating account:", error);
    }
  };

  const hasChanges = () => {
    if (!account) return false;
    return name !== (account.name || "");
  };

  // Show error if no user or no account association
  if (!user) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red">
        User not authenticated
      </Alert>
    );
  }

  if (!accountId) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red">
        No account associated with your user
      </Alert>
    );
  }

  if (loading) {
    return (
      <Stack gap="md">
        <Text>Loading account information...</Text>
      </Stack>
    );
  }

  if (!account && hasLoadedAccount) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red">
        Account data not available
      </Alert>
    );
  }

  const displayError = externalError || lastError;
  const displaySuccess = externalSuccess;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack gap="lg">
        {displayError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {displayError}
          </Alert>
        )}

        {displaySuccess && (
          <Alert icon={<IconCheck size={16} />} color="green">
            {displaySuccess}
          </Alert>
        )}

        {/* Account Information */}
        <Stack gap="md">
          <Group gap="xs" align="center">
            <IconBuilding size={20} />
            <Text size="sm" fw={500}>
              Account Information
            </Text>
          </Group>

          <TextInput
            label="Account Name"
            placeholder="Enter account name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isUpdating}
            required
            description="This is the display name for your account"
            size="md"
          />

          {/* Read-only account details */}
          {account && (
            <Stack gap="xs">
              {account.countryId && (
                <TextInput
                  label="Country"
                  value={account.countryId}
                  readOnly
                  variant="filled"
                  size="md"
                />
              )}
            </Stack>
          )}
        </Stack>

        {/* Submit Button */}
        <Group justify="flex-end" mt="lg">
          <AppButton
            type="submit"
            loading={isUpdating}
            disabled={!hasChanges() || isUpdating || !name.trim()}
            iconName="Check"
          >
            Save
          </AppButton>
        </Group>
      </Stack>
    </Box>
  );
};
